import {
  type TemplateVariable,
  TemplateVariableType,
} from "@/shared/domain/agents";

interface SeedOptions {
  /** Variables declared on the agent. Names + types come from the
   *  server's response; the editor fills the rest. */
  variables: TemplateVariable[];
  /** Optional dataset id pre-bound via the launch URL's ``?dataset_id``
   *  query param (set by the dataset-page "Run agent" dialog). Empty
   *  string means "no pre-fill". */
  datasetIdFromUrl: string;
}

/**
 * Returns the next values map for the Launch page given the previous
 * values and the agent metadata. Pure (no React, no state hooks) so
 * the seed rules can be unit-tested without mounting the page.
 *
 * Two ordered sources fill a still-empty variable:
 *
 * 1. The URL's ``dataset_id`` query param, applied to every
 *    DATASET_ID-typed variable. The user just picked this dataset
 *    explicitly; the agent's design-time default is a fallback.
 * 2. The variable's declared default. Pre-fills any remaining empty
 *    inputs so the launcher isn't asked to retype something the agent
 *    author already chose for them.
 *
 * Already-set values (anything the user typed, plus prior seeds) are
 * never clobbered — the same "only if empty" gate covers both new
 * arrivals of ``agentQuery.data`` (e.g. a stale cache followed by a
 * fresh refetch) and re-renders triggered by the URL param changing.
 *
 * Returns ``prev`` unchanged when nothing would be written, so the
 * caller's setState short-circuits without a re-render.
 */
export function seedLaunchValues(
  prev: Record<string, string>,
  options: SeedOptions,
): Record<string, string> {
  let changed = false;
  const next = { ...prev };

  for (const variable of options.variables) {
    const currentValue = next[variable.name] ?? "";
    if (currentValue !== "") {
      continue;
    }
    if (
      variable.type === TemplateVariableType.DATASET_ID &&
      options.datasetIdFromUrl !== ""
    ) {
      next[variable.name] = options.datasetIdFromUrl;
      changed = true;
      continue;
    }
    const defaultValue = variable.default;
    // Treat ``""`` as "no default" so we don't loop assigning an empty string
    // back into an empty slot on every render — that would flip ``changed``
    // each pass and defeat the setState short-circuit below.
    if (
      defaultValue !== null &&
      defaultValue !== undefined &&
      defaultValue !== ""
    ) {
      next[variable.name] = defaultValue;
      changed = true;
    }
  }

  return changed ? next : prev;
}
